/**
 * @module Prerequisite picker shared by the activity editor and the new-activity flow: link
 * existing activities, type a new prerequisite step, and see each prerequisite's completion state.
 * The server validates the list (no self-links, missing IDs, other workspaces or cycles) and
 * creates new steps; this component only collects and shows choices.
 */
import { useState } from 'react';
import type { TaskUI } from '@fluxability/types';
import { CheckCircle2, Circle, Clock, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/** Inputs of the prerequisite picker; the parent owns the list and how it is saved. */
export interface PrerequisitePickerProps {
  /** Activities that may be picked; the parent leaves out the activity itself. */
  options: TaskUI[];
  /** Linked prerequisite activities, in stored order. */
  selected: TaskUI[];
  /** Titles of new steps waiting to be created on save; defaults to none. */
  pendingSteps?: string[];
  /** Planned start of the activity, shown as "Scheduled after" when set; defaults to unscheduled. */
  scheduledStart?: string | null;
  /** Disables the step input while a save is in flight. Defaults to false. */
  busy?: boolean;
  /** Error from the last attempt to add a step, shown under the input. */
  error?: string | null;
  /** Link an existing activity by ID. */
  onAdd: (id: string) => void;
  /** Unlink an existing activity by ID. */
  onRemove: (id: string) => void;
  /** Add a new step with the typed, trimmed title. */
  onAddStep: (title: string) => void;
  /** Drop a pending step by its index in `pendingSteps`; omit when steps are created at once. */
  onRemoveStep?: (index: number) => void;
}

/** Lists prerequisites with their state and offers an existing-activity select and a new-step input. */
export function PrerequisitePicker({
  options,
  selected,
  pendingSteps = [],
  scheduledStart = null,
  busy = false,
  error = null,
  onAdd,
  onRemove,
  onAddStep,
  onRemoveStep,
}: PrerequisitePickerProps) {
  const [stepTitle, setStepTitle] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const selectedIds = new Set(selected.map(task => task.id));
  const submitStep = () => {
    const title = stepTitle.trim();
    if (!title || busy) return;
    onAddStep(title);
    setStepTitle('');
  };
  const titles = [
    ...selected.map(task => task.title || 'Untitled activity'),
    ...pendingSteps,
  ];

  return (
    <div className="mt-4 grid gap-3 border-t pt-4">
      <div>
        <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Prerequisites</h4>
        <p className="text-xs text-muted-foreground">
          Actions that must finish first. The scheduler places this activity after every unfinished one.
        </p>
      </div>
      <Select
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        value=""
        onValueChange={value => value && onAdd(value)}
      >
        <SelectTrigger aria-label="Add a prerequisite activity">
          <SelectValue placeholder="Add a prerequisite activity" />
        </SelectTrigger>
        <SelectContent>
          {pickerOpen &&
            options
              .filter(candidate => !selectedIds.has(candidate.id))
              .map(candidate => (
                <SelectItem key={candidate.id} value={candidate.id}>
                  {candidate.title || 'Untitled activity'}
                </SelectItem>
              ))}
        </SelectContent>
      </Select>
      <div className="flex gap-2">
        <Input
          value={stepTitle}
          onChange={event => setStepTitle(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              submitStep();
            }
          }}
          placeholder="New step, e.g. Buy ingredients"
          aria-label="New prerequisite step"
          maxLength={300}
          disabled={busy}
        />
        <Button type="button" variant="outline" onClick={submitStep} disabled={busy || !stepTitle.trim()}>
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
          Add step
        </Button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      {titles.length > 0 ? (
        <div className="grid gap-2">
          {scheduledStart && (
            <p className="text-xs text-muted-foreground">Scheduled after: {titles.join(', ')}</p>
          )}
          {selected.map(prerequisite => (
            <div
              key={prerequisite.id}
              className="flex items-center justify-between gap-3 rounded-md border bg-background px-3 py-2 text-sm"
            >
              <span className="flex min-w-0 items-center gap-2">
                <PrerequisiteStateIcon task={prerequisite} />
                <span className="min-w-0 truncate">{prerequisite.title || 'Untitled activity'}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{stateLabel(prerequisite)}</span>
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => onRemove(prerequisite.id)}
                aria-label={`Remove ${prerequisite.title || 'activity'} prerequisite`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          {pendingSteps.map((title, index) => (
            <div
              key={`${index}-${title}`}
              className="flex items-center justify-between gap-3 rounded-md border border-dashed bg-background px-3 py-2 text-sm"
            >
              <span className="flex min-w-0 items-center gap-2">
                <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 truncate">{title}</span>
                <span className="shrink-0 text-xs text-muted-foreground">New step · 15 min</span>
              </span>
              {onRemoveStep && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => onRemoveStep(index)}
                  aria-label={`Remove ${title} step`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
          No prerequisites.
        </div>
      )}
    </div>
  );
}

/** Done, scheduled, or open marker for one prerequisite row. */
function PrerequisiteStateIcon({ task }: { /** Prerequisite whose state is shown. */ task: TaskUI }) {
  if (isDone(task)) return <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />;
  if (task.scheduledStart) return <Clock className="h-4 w-4 shrink-0 text-sky-600" />;
  return <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />;
}

/** True for a completed or legacy-done activity. */
function isDone(task: TaskUI): boolean {
  return task.status === 'completed' || task.status === 'done';
}

/** Short state text for a prerequisite row: Done, the scheduled start, or Not scheduled. */
function stateLabel(task: TaskUI): string {
  if (isDone(task)) return 'Done';
  if (task.status === 'cancelled') return 'Cancelled';
  if (task.scheduledStart) {
    const start = new Date(task.scheduledStart);
    if (!Number.isNaN(start.getTime())) {
      return start.toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
    }
  }
  return 'Not scheduled';
}
